























import { Component } from 'vue-property-decorator'
import { Chart } from 'highcharts-vue'
import { BaseQuestion } from '@/components/Survey/QuestionTypes/BaseQuestion'
import {
  generateIndexSeries,
  getCategoriesForSlicedVariantQuestion,
  generateSlicedSeries,
  calcPercentage,
  getResponseTotalsForOptions,
  Series,
  SeriesData,
  getCategoriesForSlicedQuestion
} from '@/components/Survey/Utilities/ChartUtilities'
import {
  getOptionOrder,
  getOptionsTextFromQuestion,
  isOptionShuffled
} from '@/components/Survey/Utilities/SurveyUtilities'
import { isEndOption, sortSelectQuestionSeries } from '@/components/Survey/Utilities/ChartSortingUtilities'
import Tabs from '@/components/Tabs/Tabs.vue'
import Tab from '@/components/Tabs/Tab.vue'
import OpenEndsCard from '@/components/Survey/TextResponses/OpenEndsCard.vue'
import {
  indexMultiSelectChartToolTipFormatter,
  slicedChartTooltipFormatter
} from '@/components/Survey/Utilities/ChartTooltipUtilities'
import SliceNotSupported from '@/components/Survey/SliceNotSupported.vue'

@Component({
  components: {
    Highcharts: Chart,
    Tabs,
    Tab,
    OpenEndsCard,
    SliceNotSupported
  }
})
export default class MultiSelectQuestion extends BaseQuestion {
  readonly otherCategory: string[] = ['Other']

  get indexSeriesData () : SeriesData[] {
    const totals = getResponseTotalsForOptions(this.indexResponses)
    const denominator = this.indexResponses.length
    return getOptionsTextFromQuestion(this.question).map(category => {
      return {
        name: category.text,
        y: calcPercentage(totals[category.text], denominator)
      }
    })
  }

  get sortedIndexSeriesData () {
    const seriesData: SeriesData[] = []
    const lockedSeriesData: { order: number, data: SeriesData }[] = []
    const endOptions: SeriesData[] = []

    this.indexSeriesData.forEach((category) => {
      const data = {
        ...category,
        color: this.colorConfiguration.monochromatic
      }
      if (isEndOption(category.name)) {
        // End options always go at the end regardless of sorting
        endOptions.push(data)
      } else if (isOptionShuffled(category.name, this.questionOptions)) {
        // Shuffled options can be sorted
        seriesData.push(data)
      } else {
        // Non shuffled options are "locked" by the test creators and should remain in the original order
        const order = getOptionOrder(category.name, this.questionOptions)
        lockedSeriesData.push({ order, data })
      }
    })

    return sortSelectQuestionSeries(seriesData, lockedSeriesData, [], endOptions)
  }

  getSlicedSeries () {
    const seriesData = generateSlicedSeries(
      this.segmentTotals,
      this.colorConfiguration.qualitativePalette,
      this.sortedCategories
    )

    const sortedData: Series[] = []
    // Respect the index series sorting for sliced data
    this.sortedCategories.forEach(category => {
      const series = seriesData.find(o => o.name === category)
      if (series) {
        sortedData.push(series)
      }
    })

    return sortedData
  }

  get series () {
    if (this.isSliced || this.isVariant) {
      return this.getSlicedSeries()
    } else {
      return generateIndexSeries(this.sortedIndexSeriesData)
    }
  }

  get chartOptions () {
    return {
      ...this.defaultChartOptions(this.displayCategories),
      caption: {
        text: 'Multi-select is calculated by combining the number of responses per category out of the total number of participants.'
      },
      series: this.series,
      xAxis: {
        ...this.defaultChartOptions(this.displayCategories).xAxis,
        categories: this.displayCategories
      },
      tooltip: this.isSliced || this.isVariant
        ? slicedChartTooltipFormatter(this.segmentTotals, this.question.type, this.sliceByQuestion?.type)
        : indexMultiSelectChartToolTipFormatter(this.indexTotals)
    }
  }

  get sortedCategories () {
    return this.sortedIndexSeriesData.map(data => data.name!)
  }

  get displayCategories () {
    if (this.isSliced && this.isVariant) {
      return getCategoriesForSlicedVariantQuestion(this.typedSlicedVariantResponses, this.selectedTab)
    } else if (this.isSliced || this.isVariant) {
      return getCategoriesForSlicedQuestion(this.segmentTotals, this.sliceByQuestion)
    } else {
      return this.sortedCategories
    }
  }

  get questionOptions () {
    return this.question!.options!
  }
}
